import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import ScrollToTop from "@/components/ScrollToTop";
import LoadingScreen from "@/components/LoadingScreen";
import { LoadFailure, RouteErrorBoundary, RouteReady } from "@/components/RouteRecovery";
import { HOME_READY_STEPS, HomeReadyProvider, type HomeReadyStep } from "@/components/home/HomeReadyContext";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

const Index           = lazy(() => import("./pages/Index"));
const Projects        = lazy(() => import("./pages/Projects"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const About           = lazy(() => import("./pages/About"));
const Contact         = lazy(() => import("./pages/Contact"));
const NotFound        = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();
const LOADER_MIN_MS = 2400;
const LOADER_MAX_MS = 4500;
const ROUTE_TIMEOUT_MS = 15000;
const noop = () => undefined;

function PendingRoute({ initialLoading }: { initialLoading: boolean }) {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), ROUTE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);
  if (timedOut) return <LoadFailure />;
  return initialLoading ? null : <LoadingScreen onReady={noop} />;
}

const AppShell = () => {
  const location = useLocation();
  const isHomeRoute = location.pathname === "/";
  const [loading, setLoading] = useState(true);
  const [loaderAssetsReady, setLoaderAssetsReady] = useState(false);
  const [minimumElapsed, setMinimumElapsed] = useState(false);
  const [safetyElapsed, setSafetyElapsed] = useState(false);
  const [resolvedPath, setResolvedPath] = useState<string | null>(null);
  const [initialReadinessTimedOut, setInitialReadinessTimedOut] = useState(false);
  const [initialLoaderExited, setInitialLoaderExited] = useState(false);
  const [initialRevealComplete, setInitialRevealComplete] = useState(false);
  useBodyScrollLock(!initialRevealComplete);
  const [homeReadiness, setHomeReadiness] = useState<{ path: string; steps: Set<HomeReadyStep> }>(
    () => ({ path: location.pathname, steps: new Set() }),
  );
  const hideRequestedRef = useRef(false);
  const handleRouteReady = useCallback((path: string) => setResolvedPath(path), []);

  const markStepReady = useCallback((step: HomeReadyStep, ready = true) => {
    setHomeReadiness((prev) => {
      const steps = prev.path === location.pathname ? prev.steps : new Set<HomeReadyStep>();
      if (prev.path === location.pathname && steps.has(step) === ready) return prev;
      const next = new Set(steps);
      if (ready) next.add(step);
      else next.delete(step);
      return { path: location.pathname, steps: next };
    });
  }, [location.pathname]);
  const homeReadyValue = useMemo(() => ({ markStepReady }), [markStepReady]);

  const handleLoaderReady = useCallback(() => {
    setLoaderAssetsReady(true);
  }, []);

  useEffect(() => {
    const minTimer = window.setTimeout(() => setMinimumElapsed(true), LOADER_MIN_MS);
    const safetyTimer = window.setTimeout(() => setSafetyElapsed(true), LOADER_MAX_MS);

    return () => {
      window.clearTimeout(minTimer);
      window.clearTimeout(safetyTimer);
    };
  }, []);

  const isHomeReady = useMemo(
    () => !isHomeRoute || (homeReadiness.path === location.pathname && HOME_READY_STEPS.every((step) => homeReadiness.steps.has(step))),
    [homeReadiness, isHomeRoute, location.pathname],
  );
  const heroMediaReady = homeReadiness.path === location.pathname && homeReadiness.steps.has('hero-media-ready');
  const showInitialLoadFailure = !initialRevealComplete && isHomeRoute && !heroMediaReady &&
    (initialReadinessTimedOut || !loading);

  useLayoutEffect(() => {
    if (initialLoaderExited && !loading && (!isHomeRoute || heroMediaReady)) {
      setInitialRevealComplete(true);
    }
  }, [initialLoaderExited, loading, isHomeRoute, heroMediaReady]);

  useEffect(() => {
    if (!loading) return;
    const timer = window.setTimeout(() => setInitialReadinessTimedOut(true), ROUTE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (!loading || hideRequestedRef.current) return;

    const routeMounted = resolvedPath === location.pathname;
    const canHideLoader = routeMounted && (!isHomeRoute || heroMediaReady) &&
      (safetyElapsed || (loaderAssetsReady && isHomeReady && minimumElapsed));
    if (!canHideLoader) return;

    hideRequestedRef.current = true;
    setLoading(false);
  }, [isHomeReady, isHomeRoute, heroMediaReady, loaderAssetsReady, loading, minimumElapsed, safetyElapsed, resolvedPath, location.pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />

        <HomeReadyProvider value={homeReadyValue}>
          <AnimatePresence mode="wait" onExitComplete={() => setInitialLoaderExited(true)}>
            {loading && !showInitialLoadFailure && <LoadingScreen key="loader" onReady={handleLoaderReady} />}
          </AnimatePresence>
          {showInitialLoadFailure && <LoadFailure />}

          <ScrollToTop />
          <RouteErrorBoundary key={location.pathname}>
          <Suspense fallback={<PendingRoute initialLoading={loading} />}>
            <RouteReady path={location.pathname} onReady={handleRouteReady}>
            <Routes>
              <Route path="/"              element={<Index />} />
              <Route path="/projects"      element={<Projects />} />
              <Route path="/projects/:id"  element={<ProjectDetailPage />} />
              <Route path="/about"         element={<About />} />
              <Route path="/contact"       element={<Contact />} />
              <Route path="*"              element={<NotFound />} />
            </Routes>
            </RouteReady>
          </Suspense>
          </RouteErrorBoundary>
        </HomeReadyProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
};

export default App;
